<template>
  <div>
    <label class="d-block">{{ $t(label) }}</label>
    <validation-provider :rules="rules" :name="$t(name)" v-slot="{ errors }">
      <v-select
        v-if="fieldType === 'select'"
        v-model="model"
        :clearable="false"
        taggable
        :multiple="multiple"
        :create-option="(text) => ({ label: text, value: text })"
        @input="$emit('input', model)"
      />
      <input
        v-else-if="fieldType === 'boolean'"
        type="checkbox"
        v-model="model"
        @change="$emit('input', model)"
      />
      <div v-else-if="fieldType === 'json'" class="json-editor">
        <div class="json-editor__toolbar">
          <small
            class="json-editor__status"
            :class="jsonError ? 'text-danger' : 'text-success'"
          >
            {{ jsonError || $t('commands.form.json_editor.valid') }}
          </small>
          <button
            type="button"
            class="btn btn-sm btn-outline-secondary"
            :disabled="Boolean(jsonError) || !model"
            @click="formatJson"
          >
            {{ $t('commands.form.json_editor.format') }}
          </button>
        </div>
        <textarea
          v-model="model"
          class="form-control json-editor__input"
          rows="10"
          spellcheck="false"
          @input="onJsonInput"
          @blur="formatJson"
        ></textarea>
      </div>
      <input
        v-else
        v-model="model"
        :type="fieldType"
        class="form-control"
        @input="inputHandler(type)"
        :disabled="type === 'radius' && context.model.location !== 'from_alarm'"
        v-properties="properties"
      />
      <p v-if="hint" class="mb-0 small">{{ $t(hint) }}</p>
      <p v-if="errors" class="small text-danger">{{ errors[0] }}</p>
    </validation-provider>
  </div>
</template>

<script>
import vSelect from "vue-select";
import { EventBus } from "@/libs/event-bus";
import { ValidationProvider } from "vee-validate";

export default {
  components: {
    vSelect,
    ValidationProvider,
  },
  props: {
    value: Array | Object,
    multiple: Boolean | Number,
    config: Object,
    type: String,
    context: Object,
  },
  data() {
    return {
      model: null,
    };
  },
  created() {
    if (this.type === "radius") {
      EventBus.on("location_field:data", this.onLocationFieldData);
      isNaN(this.model) ? 10.0: this.model;
    } else if (this.type === "loop") {
      this.model = this.value ? true : false;
      this.$emit('input', this.model);
    } else if (this.fieldType === "json" && typeof this.value === "object" && this.value !== null) {
      this.model = JSON.stringify(this.value, null, 2);
    } else {
      this.model = this.value || "";
    }
  },
  beforeDestroy() {
    EventBus.off("location_field:data");
  },
  computed: {
    label() {
      return this.config.label;
    },
    name() {
      return this.config.name;
    },
    fieldType() {
      return this.config.type;
    },
    hint() {
      return this.config.hint || null;
    },
    options() {
      return this.config.options || {};
    },
    properties() {
      return this.config.properties || [];
    },
    rules() {
      return this.config.rules || "";
    },
    alarmCompatible() {
      return this.context.flowchartConfig.alarm_compatible;
    },
    jsonError() {
      if (this.fieldType !== "json" || !this.model) return "";

      try {
        JSON.parse(this.model);
        return "";
      } catch (error) {
        return this.$t("commands.form.json_editor.invalid");
      }
    },
  },
  methods: {
    onJsonInput() {
      this.$emit("input", this.model);
    },
    formatJson() {
      if (this.fieldType !== "json" || !this.model || this.jsonError) return;

      this.model = JSON.stringify(JSON.parse(this.model), null, 2);
      this.$emit("input", this.model);
    },
    onLocationFieldData(data) {
      if (this.type === "radius") {
        this.model = data ? data.radius : null;
        this.$emit("input", this.model);
      }
    },
    inputHandler(field) {
      if (field === "radius" || field === "delay") {
        this.model = this.model > 0 ? this.model : null;
        if (this.model === null) {
          const min = this.properties.find((p) => p.name === "min");
          this.$emit("input", min ? min.value : 5);
        } else {
          this.$emit("input", this.model);
        }
      } else {
        this.$emit("input", this.model);
      }
    },
  },
};
</script>

<style scoped lang="scss">
.json-editor {
  overflow: hidden;
  border: 1px solid #ced4da;
  border-radius: 0.375rem;
  background: #fff;

  &:focus-within {
    border-color: #80bdff;
    box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.15);
  }
}

.json-editor__toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-height: 2.5rem;
  padding: 0.375rem 0.5rem 0.375rem 0.75rem;
  border-bottom: 1px solid #e9ecef;
  background: #f8f9fa;
}

.json-editor__status {
  margin-right: 0.75rem;
}

.json-editor__input {
  min-height: 13rem;
  resize: vertical;
  border: 0;
  border-radius: 0;
  font-family: Consolas, "Courier New", monospace;
  font-size: 0.875rem;
  line-height: 1.5;
  tab-size: 2;

  &:focus {
    box-shadow: none;
  }
}
</style>
